import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { PROPOSAL_ACCENT, PROPOSAL_COPY_FONT, PROPOSAL_TEXT, proposalMoney } from "@/lib/proposalTheme";
import { ChevronDown } from "lucide-react";
import type { ManualApprovedPaymentTerm } from "@shared/manualApprovedProposalWrapper";
import { withPaymentTermsDisplayFallback } from "@shared/proposalCustomerPaymentTerms";
import { formatLinkBExpiryLongLabel } from "@shared/proposalLinkBExpiry";

const SALES_TEXT = PROPOSAL_TEXT;
const SALES_ACCENT = PROPOSAL_ACCENT;
const COPY_FONT = PROPOSAL_COPY_FONT;

type ProposalRetailTermsCollapsibleProps = {
  paymentTerms: ManualApprovedPaymentTerm[];
  createdAt?: string | Date | null;
  linkBExpiresAt?: string | Date | null;
  /** When true, terms content is visible without clicking (customer proposal tab). */
  defaultExpanded?: boolean;
};

export default function ProposalRetailTermsCollapsible({
  paymentTerms,
  linkBExpiresAt,
  defaultExpanded = false,
}: ProposalRetailTermsCollapsibleProps) {
  const expiryText = formatLinkBExpiryLongLabel(linkBExpiresAt);
  const displayTerms = withPaymentTermsDisplayFallback(paymentTerms);

  return (
    <Collapsible defaultOpen={defaultExpanded} className="min-w-0 max-w-full rounded-xl border border-[#1A2744]/12 bg-white/55">
      <CollapsibleTrigger className="flex w-full items-center justify-between gap-3 px-4 py-4 text-left sm:px-6">
        <span className="text-sm font-semibold uppercase tracking-[0.16em]" style={{ color: SALES_TEXT }}>
          Terms and conditions
        </span>
        <ChevronDown className="h-4 w-4 shrink-0 text-[#1A2744]/70" />
      </CollapsibleTrigger>
      <CollapsibleContent className="border-t border-[#1A2744]/10 px-4 py-6 sm:px-8 sm:py-8">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-[0.85fr_1.15fr] lg:gap-10" style={{ fontFamily: COPY_FONT }}>
          <div className="space-y-3 lg:pr-6">
            <p className="text-xs uppercase tracking-[0.16em] font-medium" style={{ color: SALES_ACCENT }}>
              Payment
            </p>
            <h2 className="text-[1.22rem] font-medium tracking-[0.01em]" style={{ color: SALES_TEXT }}>
              Payment Terms
            </h2>
            {displayTerms.length > 0 ? (
              <div className="space-y-2">
                {displayTerms.map((term, idx) => (
                  <p key={`${term.stage}-${idx}`} className="text-[1rem] leading-8" style={{ color: `${SALES_TEXT}E6` }}>
                    {term.fixedAmount != null && term.fixedAmount > 0
                      ? `${proposalMoney.format(term.fixedAmount)} ${term.stage}`
                      : `${Math.round(term.percentage)}% ${term.stage}`}
                  </p>
                ))}
              </div>
            ) : (
              <p className="text-[1rem] leading-8" style={{ color: `${SALES_TEXT}E6` }}>
                Payment terms unavailable.
              </p>
            )}
          </div>

          <div className="space-y-4 lg:border-l lg:border-[#1A2744]/10 lg:pl-8">
            <p className="text-xs uppercase tracking-[0.16em] font-medium" style={{ color: SALES_ACCENT }}>
              Terms
            </p>
            <h2 className="text-[1.22rem] font-medium tracking-[0.01em]" style={{ color: SALES_TEXT }}>
              Terms & Conditions
            </h2>
            <ol className="max-w-[780px] list-decimal space-y-4 pl-6 text-[0.9rem] leading-7" style={{ color: `${SALES_TEXT}D9` }}>
              <li>All prices are stated in SGD.</li>
              <li>
                Clarity&rsquo;s Terms & Conditions of Sale shall apply unless otherwise agreed in writing.{" "}
                <a
                  href="/terms/retail"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-medium no-underline hover:underline"
                  style={{ color: SALES_ACCENT }}
                >
                  View full terms →
                </a>
              </li>
              <li>
                <strong>Minor Installation Accessories</strong> — Clarity may supply and charge for minor accessories
                reasonably required to complete the installation, up to the lower of 0.5% of the Proposal&rsquo;s Final
                Price or S$500, without further approval.{" "}
                <a
                  href="/terms/retail#minor-installation-accessories"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-medium no-underline hover:underline"
                  style={{ color: SALES_ACCENT }}
                >
                  View full terms →
                </a>
              </li>
              <li>Labour required for cable laying is not included unless otherwise stated.</li>
              <li>
                Systems are custom designed and specified based on the Customer&rsquo;s requirements, space, expectations,
                and budget. Alternatives are available upon request.
              </li>
            </ol>
            <div className="rounded-md border border-[#1A2744]/10 bg-[#FAFAF7] px-4 py-3 text-sm">
              This proposal is valid until {expiryText}.
            </div>
          </div>
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}
